import { BuildingDesign } from '@shared/models/design/building-design.model';
import { calculatedAreaActivityTypes } from '@shared/models/design/activities/activity-type.model';
import { addActivityReferenceByType } from './mutators/design.mutator';
import { getId } from '../object.helpers';
import { getActivityByType } from './activities.helpers';
import { createActivityOfType } from './factories/activity.factory';
import { throwIfError } from '../utils.helpers';
import {
  getAllStoreys,
  healStoreyCollections,
  mapStoreysInDesign,
} from './storey.helpers';

/**
 * Ensures every {@link BuildingDesign} has all calculated area activities and storey references.
 * Calculated references do not have shares, so adding them does not redistribute program areas.
 * Storey refs are applied across every {@link Building} (DEV-2576).
 */
export const applyMissingCalculatedAreaActivities = (
  design: BuildingDesign,
): BuildingDesign => {
  // Incomplete JSON (missing storey shapes/activities) must not throw in find/PaginationDB.
  design = healStoreyCollections(design);
  for (const activity of calculatedAreaActivityTypes) {
    let designActivity = getActivityByType(design, activity);

    if (!designActivity) {
      designActivity = throwIfError(createActivityOfType(activity));
      design = {
        ...design,
        activities: [...design.activities, designActivity],
      };
    }

    const storeyIdsWithoutActivity = getAllStoreys(design)
      .filter(
        (storey) => !storey.activities.some((ref) => ref.type === activity),
      )
      .map(getId);

    design = addActivityReferenceByType(
      design,
      activity,
      ...storeyIdsWithoutActivity,
    );

    let changedStoreyRefs = false;
    const nextDesign = mapStoreysInDesign(design, (storey) => {
      let changedStorey = false;
      const refs = storey.activities.map((ref) => {
        if (ref.type !== activity || ref.activity_id === designActivity.id)
          return ref;

        changedStorey = true;
        changedStoreyRefs = true;
        return {
          ...ref,
          activity_id: designActivity.id,
        };
      });

      return changedStorey ? { ...storey, activities: refs } : storey;
    });

    if (changedStoreyRefs) {
      design = nextDesign;
    }
  }

  return design;
};
